import EntityBadge from '../EntityBadge.jsx';
import { formatCurrency } from '../../lib/format.js';

// Badge de type source (recurrence / future_expense / monthly_budget)
const SOURCE_KIND_LABELS = {
  recurrence: { label: 'Récurrence', className: 'bg-violet-100 text-violet-700 ring-violet-200' },
  future_expense: { label: 'Ponctuel', className: 'bg-sky-100 text-sky-700 ring-sky-200' },
  monthly_budget: { label: 'Budget', className: 'bg-amber-100 text-amber-700 ring-amber-200' },
};

function SourceKindBadge({ kind }) {
  const cfg = SOURCE_KIND_LABELS[kind] ?? {
    label: kind ?? '—',
    className: 'bg-slate-100 text-slate-600 ring-slate-200',
  };
  return (
    <span
      className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset ${cfg.className}`}
    >
      {cfg.label}
    </span>
  );
}

function EntityCell({ entityId, entityMap }) {
  const e = entityMap instanceof Map ? entityMap.get(entityId) : entityMap[entityId];
  return <EntityBadge slug={e?.slug} name={e?.name ?? entityId} />;
}

function formatDateShort(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}`;
}

function lookupCategory(categoryId, categories) {
  if (!categories || !categoryId) return categoryId ?? '—';
  const cat = categories.find((c) => c.id === categoryId);
  return cat?.name ?? categoryId;
}

/**
 * Section lecture seule affichant les événements de refacturation projetés
 * depuis les prévisionnels (avant cristallisation du mois).
 *
 * Props :
 * - projectedEvents : array d'events projetés (shape définie par GET /v1/intercompany/periods/:yyyymm)
 * - entityMap       : Map<entity_id, { id, slug, name }> ou objet plain
 * - categories      : array de { id, name, … }
 */
export default function ProjectedEventsSection({
  projectedEvents = [],
  entityMap = {},
  categories = [],
}) {
  if (projectedEvents.length === 0) {
    return (
      <div className="py-6 text-center space-y-1">
        <p className="text-sm text-slate-500">
          Aucune refacturation projetée pour ce mois.
        </p>
        <p className="text-xs text-slate-400 max-w-sm mx-auto">
          Configure une dépense prévisionnelle avec refacturation pour qu&apos;elle apparaisse ici.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Hint */}
      <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2.5 text-xs text-sky-700 leading-relaxed">
        Ces refacturations sont calculées en temps réel depuis tes prévisionnels.
        Clique sur <strong>«&nbsp;Cristalliser le mois&nbsp;»</strong> pour les figer en écritures
        comptables (et désactiver la projection live pour ce mois).
      </div>

      {/* Table */}
      <div className="overflow-x-auto -mx-1">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left text-xs font-medium text-slate-500 px-3 py-2.5 sticky left-0 bg-slate-50 whitespace-nowrap">
                Date
              </th>
              <th className="text-left text-xs font-medium text-slate-500 px-3 py-2.5 min-w-[140px]">
                Source
              </th>
              <th className="text-left text-xs font-medium text-slate-500 px-3 py-2.5 whitespace-nowrap">
                Émettrice
              </th>
              <th className="text-left text-xs font-medium text-slate-500 px-3 py-2.5 whitespace-nowrap">
                Débitrice
              </th>
              <th className="text-left text-xs font-medium text-slate-500 px-3 py-2.5 min-w-[120px] hidden md:table-cell">
                Catégorie
              </th>
              <th className="text-right text-xs font-medium text-slate-500 px-3 py-2.5 whitespace-nowrap">
                HT
              </th>
              <th className="text-right text-xs font-medium text-slate-500 px-3 py-2.5 whitespace-nowrap hidden lg:table-cell">
                TVA
              </th>
              <th className="text-right text-xs font-medium text-slate-500 px-3 py-2.5 whitespace-nowrap">
                TTC
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {projectedEvents.map((ev, idx) => (
              <tr
                key={`${ev.source_item_id ?? idx}-${ev.to_entity_id}`}
                className="hover:bg-slate-50"
              >
                {/* Date JJ/MM */}
                <td className="px-3 py-2.5 sticky left-0 bg-white hover:bg-slate-50 text-xs text-slate-500 tabular-nums whitespace-nowrap">
                  {formatDateShort(ev.date)}
                </td>

                {/* Source : badge type + label */}
                <td className="px-3 py-2.5 min-w-[140px]">
                  <div className="flex flex-col gap-0.5">
                    <SourceKindBadge kind={ev.source_kind} />
                    <span className="text-xs text-slate-700 leading-tight">
                      {ev.source_label ?? '—'}
                    </span>
                  </div>
                </td>

                {/* Émettrice */}
                <td className="px-3 py-2.5">
                  <EntityCell entityId={ev.from_entity_id} entityMap={entityMap} />
                </td>

                {/* Débitrice */}
                <td className="px-3 py-2.5">
                  <EntityCell entityId={ev.to_entity_id} entityMap={entityMap} />
                </td>

                {/* Catégorie */}
                <td className="px-3 py-2.5 text-xs text-slate-600 min-w-[120px] hidden md:table-cell">
                  {lookupCategory(ev.category_id, categories)}
                </td>

                {/* HT */}
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-700 whitespace-nowrap font-medium">
                  {formatCurrency(ev.amount_ht_cents)}
                </td>

                {/* TVA : taux + montant */}
                <td className="px-3 py-2.5 text-right whitespace-nowrap hidden lg:table-cell">
                  <span className="tabular-nums text-slate-500 text-xs">
                    {ev.vat_rate != null ? `${ev.vat_rate} %` : '—'}
                  </span>
                  <br />
                  <span className="tabular-nums text-slate-400 text-xs">
                    {formatCurrency(ev.vat_cents)}
                  </span>
                </td>

                {/* TTC */}
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-900 whitespace-nowrap font-semibold">
                  {formatCurrency(ev.total_ttc_cents)}
                </td>
              </tr>
            ))}
          </tbody>

          {/* Ligne total */}
          {projectedEvents.length > 1 && (
            <tfoot>
              <tr className="border-t-2 border-slate-200 bg-slate-50">
                <td
                  colSpan={5}
                  className="px-3 py-2 text-xs font-medium text-slate-500 sticky left-0 bg-slate-50"
                >
                  Total ({projectedEvents.length} lignes)
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-700 font-medium text-xs whitespace-nowrap">
                  {formatCurrency(
                    projectedEvents.reduce((s, e) => s + (e.amount_ht_cents ?? 0), 0)
                  )}
                </td>
                <td className="px-3 py-2 hidden lg:table-cell" />
                <td className="px-3 py-2 text-right tabular-nums text-slate-900 font-semibold text-xs whitespace-nowrap">
                  {formatCurrency(
                    projectedEvents.reduce((s, e) => s + (e.total_ttc_cents ?? 0), 0)
                  )}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
